import { useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { WorkTableSort } from "../features/works/types/work-table";

type SortOrder = "asc" | "desc";

type UseWorksSearchParamsOptions = {
  defaultSortBy?: WorkTableSort;
  defaultSortOrder?: SortOrder;
};

export const useWorksSearchParams = (options?: UseWorksSearchParamsOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultSortBy = options?.defaultSortBy ?? WorkTableSort.StartDate;
  const defaultSortOrder = options?.defaultSortOrder ?? "asc";

  const pageRaw = Number(searchParams.get("page") ?? 1);
  const page = Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;

  const sortBy =
    (searchParams.get("sortBy") as WorkTableSort | null) ?? defaultSortBy;

  const sortOrder =
    (searchParams.get("sortOrder") as SortOrder | null) ?? defaultSortOrder;

  useEffect(() => {
    const next = new URLSearchParams(searchParams);

    if (!searchParams.get("page")) {
      next.set("page", "1");
    }

    if (!searchParams.get("sortBy")) {
      next.set("sortBy", defaultSortBy);
    }

    if (!searchParams.get("sortOrder")) {
      next.set("sortOrder", defaultSortOrder);
    }

    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [defaultSortBy, defaultSortOrder, searchParams, setSearchParams]);

  const updateParams = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(searchParams);

      Object.entries(patch).forEach(([key, value]) => {
        if (value === null || value === undefined || value === "") {
          next.delete(key);
        } else {
          next.set(key, value);
        }
      });

      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (nextPage: number) => {
      updateParams({ page: String(nextPage) });
    },
    [updateParams],
  );

  const setSort = useCallback(
    (nextSortBy: WorkTableSort, nextSortOrder: SortOrder) => {
      updateParams({
        sortBy: nextSortBy,
        sortOrder: nextSortOrder,
        page: "1",
      });
    },
    [updateParams],
  );

  return {
    page,
    sortBy,
    sortOrder,
    setPage,
    setSort,
    updateParams,
  };
};
